import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Badge, Tooltip } from 'antd';
import {
	DeleteOutlined,
	SwapOutlined,
	CloseCircleFilled,
	CheckCircleFilled,
	ThunderboltFilled,
	InfoCircleFilled,
	FileTextOutlined,
} from '@ant-design/icons';

interface ActionStripProps {
	selectedCount: number;
	onDelete: () => void;
	onStatusChange: (status: string) => void;
	onCancel: () => void;
	availableStatuses: string[];
}

const ActionStrip: React.FC<ActionStripProps> = ({
	selectedCount,
	onDelete,
	onStatusChange,
	onCancel,
	availableStatuses,
}) => {
	const [visible, setVisible] = useState(false);
	const [actionFeedback, setActionFeedback] = useState<string | null>(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			setVisible(true);
		}, 50);

		return () => clearTimeout(timer);
	}, []);

	const statusIconMap: Record<string, React.ReactNode> = {
		NEW: <InfoCircleFilled style={{ color: '#1890ff' }} />,
		CONTACTED: <CheckCircleFilled style={{ color: '#52c41a' }} />,
		PROCESSING: <ThunderboltFilled style={{ color: '#fa8c16' }} />,
		FAILED: <CloseCircleFilled style={{ color: '#f5222d' }} />,
	};

	const statusItems = availableStatuses.map((status) => ({
		key: status,
		label: (
			<div className="flex items-center gap-2 py-1">
				{statusIconMap[status]}
				<span>{status}</span>
			</div>
		),
		onClick: () => {
			onStatusChange(status);
			showActionFeedback(`Moving ${selectedCount} leads to ${status}`);
		},
	}));

	const handleDelete = () => {
		onDelete();
		showActionFeedback(`Deleting ${selectedCount} leads`);
	};

	const showActionFeedback = (text: string) => {
		setActionFeedback(text);
		setTimeout(() => setActionFeedback(null), 3000);
	};

	return (
		<div
			className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-white rounded-xl"
			style={{
				width: '420px',
				height: '64px', // Reduced height from 76px to 64px
				zIndex: 1000,
				opacity: visible ? 1 : 0,
				transform: `translate(-50%, ${visible ? '0' : '50px'})`,
				transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
				boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
				border: '1px solid rgba(240, 240, 240, 1)',
			}}
		>
			{/* Left side accent */}
			<div
				className="absolute left-0 top-0 bottom-0 rounded-l-xl"
				style={{
					width: '4px',
					background: 'linear-gradient(to bottom, #6366f1, #3b82f6)',
				}}
			/>

			{/* Action feedback overlay */}
			{actionFeedback && (
				<div
					className="absolute inset-0 flex items-center justify-center rounded-xl bg-white bg-opacity-90"
					style={{
						animation: 'fadeIn 0.2s ease-out',
						zIndex: 10,
					}}
				>
					<div className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
						<div className="animate-spin text-blue-500">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
							</svg>
						</div>
						<span className="font-medium text-blue-700">{actionFeedback}</span>
					</div>
				</div>
			)}

			<div className="flex items-center justify-between px-5 py-0 h-full relative overflow-hidden">
				{/* Selection count with badge - changed icon */}
				<div className="flex items-center">
					<div
						className="bg-indigo-50 rounded-full p-2 flex items-center justify-center"
						style={{
							width: '42px',
							height: '42px',
							boxShadow: '0 2px 5px rgba(99, 102, 241, 0.1)',
						}}
					>
						<Badge
							count={selectedCount}
							overflowCount={999}
							offset={[5, 5]}
							style={{
								backgroundColor: '#6366f1',
								boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.9)',
								fontWeight: '600',
							}}
						>
							<FileTextOutlined style={{ fontSize: '20px', color: '#6366f1' }} />
						</Badge>
					</div>
				</div>

				{/* Action buttons - increased spacing */}
				<div className="flex items-center gap-6 pr-1">
					{' '}
					{/* Increased gap from 4 to 6 */}
					<Tooltip title="Delete selected leads">
						<Button
							danger
							icon={<DeleteOutlined />}
							onClick={handleDelete}
							shape="round"
							className="flex items-center justify-center hover:scale-105 transition-all"
							style={{
								fontWeight: '500',
								boxShadow: '0 2px 6px rgba(244, 67, 54, 0.15)',
								width: '92px',
								height: '34px',
							}}
						>
							Delete
						</Button>
					</Tooltip>
					<Dropdown
						menu={{ items: statusItems }}
						placement="topCenter"
						trigger={['click']}
						overlayStyle={{ minWidth: '150px' }}
						overlayClassName="leads-status-dropdown"
					>
						<Button
							type="primary"
							icon={<SwapOutlined />}
							shape="round"
							className="flex items-center justify-center hover:shadow-lg hover:scale-105 transition-all"
							style={{
								fontWeight: '500',
								background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
								borderColor: 'transparent',
								boxShadow: '0 2px 6px rgba(99, 102, 241, 0.25)',
								width: '104px',
								height: '34px',
							}}
						>
							Move To
						</Button>
					</Dropdown>
					<Tooltip title="Cancel selection">
						<Button
							icon={<CloseCircleFilled style={{ color: '#9ca3af', fontSize: '18px' }} />}
							onClick={onCancel}
							type="text"
							shape="circle"
							className="hover:bg-gray-50 hover:scale-110 transition-all flex items-center justify-center"
							style={{
								width: '36px',
								height: '36px',
								boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
							}}
						/>
					</Tooltip>
				</div>
			</div>

			{/* Subtle bottom pill indicator */}
			<div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-200 rounded-full" />

			{/* CSS for animation */}
			<style
				dangerouslySetInnerHTML={{
					__html: `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `,
				}}
			/>
		</div>
	);
};

export default ActionStrip;
